import { canEditDeviceLimits } from '../../lib/limit-domains';
/**
 * Status Icons Component
 * - display a row of icons indicating current system state below current readings
 * - hover over icons to display a status message on the thermostat indicating a description of the icon
 * - only the main component is exported
 */
import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { StatusMessage } from './status-message';
import { ResumeSchedule } from './resume-schedule';
import { UnknownStatusIcon } from './unknown-status-icon';
import { Thermostat } from '../../../../../../../types/generated-types';
import './status-icons.css';
import { visibleLocalPermissions } from '../../lib/local-permissions';
import {
  formatObservationEvidence,
  getMetricObservedAt,
  usesObservationEvidence,
  unavailableObservationScope,
} from '../../lib/observation-freshness';

interface StatusIconProps {
  thermostat?: Partial<Thermostat>;
  preferredUnits?: 'C' | 'F';
  statusMessage: string;
  observationNow?: number;
  showStatusMessage: (message: string) => void;
  showResumeSchedule: boolean;
  briefScheduleStatus?: string;
  supportsScheduling?: boolean;
  values: {
    CurrentAttributes: {
      minCoolSetpointLimit: number | undefined;
      maxCoolSetpointLimit: number | undefined;
      minHeatSetpointLimit: number | undefined;
      maxHeatSetpointLimit: number | undefined;
      operatingMode: string;
    };
  };
}

/* Unicode Constants */
const BLANK = '\u00a0';

// MHK2 Operation Manual (2025), p. 19. Display-only defaults in Fahrenheit,
// not observed MHK2 settings, equipment capabilities, or command-validation limits.
const MITSUBISHI_DEFAULT_RANGE_STOPS_F = {
  Heat: '40-90',
  Cool: '50-99',
} as const;

function mitsubishiDefaultRangeStopHelp(operatingMode: string | undefined) {
  const { Heat, Cool } = MITSUBISHI_DEFAULT_RANGE_STOPS_F;
  if (operatingMode === 'Heat' || operatingMode === 'Cool') {
    return `Mitsubishi default range stops: ${operatingMode} (${MITSUBISHI_DEFAULT_RANGE_STOPS_F[operatingMode]}\u2109)`;
  }
  if (operatingMode === 'Auto') {
    return `Mitsubishi default range stops: Auto (H ${Heat} / C ${Cool}\u2109)`;
  }
  return 'No active heat/cool range stops.';
}

/* Main Component */
export function StatusIcons(props: StatusIconProps) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    thermostat,
    statusMessage,
    showStatusMessage: publishStatusMessage,
    showResumeSchedule,
    briefScheduleStatus,
    supportsScheduling = true,
  } = props;
  const [activeStatus, setActiveStatus] = React.useState<
    'hvac' | 'fan' | 'rangeStops' | 'permissions'
  >();
  const showStatusMessage = (message: string) => {
    setActiveStatus(undefined);
    publishStatusMessage(message);
  };
  const evidence = (key: 'operatingState' | 'fanState') =>
    thermostat &&
    !thermostat.isMitsubishiThermostat &&
    usesObservationEvidence(thermostat)
      ? ` ${thermostat.supportsObservationFreshness === null ? `${unavailableObservationScope}. Retained observation` : 'Last successful observation'}: ${formatObservationEvidence(getMetricObservedAt(thermostat, key), props.observationNow ?? Date.now())}.`
      : '';
  const hvacHelp =
    (thermostat?.hvacIconHelpText || 'Heating/cooling activity unknown') +
    evidence('operatingState');
  const fanHelp =
    (thermostat?.fanIconHelpText || 'Fan-running status') +
    evidence('fanState');
  const showPermissionStatus =
    !thermostat?.isMitsubishiThermostat ||
    visibleLocalPermissions(thermostat?.localControlPermissionsV1?.value)
      .length > 0;
  const permissionHelp = showPermissionStatus
    ? thermostat?.keypadLockoutIconHelpText ||
      (thermostat?.isMitsubishiThermostat
        ? 'Local controller permissions have not been reported.'
        : BLANK)
    : BLANK;
  const useManualMitsubishiRangeStops =
    thermostat?.isMitsubishiThermostat === true &&
    !canEditDeviceLimits(thermostat);
  const profileHelp = () => {
    const values = props.values.CurrentAttributes;
    const units = props.preferredUnits === 'C' ? '\u2103' : '\u2109';
    const range = (mode: 'Heat' | 'Cool') =>
      `${values[`min${mode}SetpointLimit`] ?? 'unavailable'}-${values[`max${mode}SetpointLimit`] ?? 'unavailable'}`;
    const help = thermostat?.profileIconHelpText || '';
    if (values.operatingMode === 'Heat' || values.operatingMode === 'Cool')
      return `${help} ${values.operatingMode} (${range(values.operatingMode)}${units})`;
    if (values.operatingMode === 'Auto')
      return `${help} Auto (H ${range('Heat')} / C ${range('Cool')}${units})`;
    return 'Thermostat currently has no profile set.';
  };
  const showMitsubishiRangeStops =
    thermostat?.isMitsubishiThermostat === true &&
    activeStatus === 'rangeStops';
  const wrapStatus =
    usesObservationEvidence(thermostat) || showMitsubishiRangeStops;
  const displayedMessage =
    activeStatus === 'hvac'
      ? hvacHelp
      : activeStatus === 'fan'
        ? fanHelp
        : activeStatus === 'permissions'
          ? permissionHelp
          : showMitsubishiRangeStops
            ? useManualMitsubishiRangeStops
              ? mitsubishiDefaultRangeStopHelp(
                  props.values?.CurrentAttributes?.operatingMode,
                )
              : profileHelp()
            : statusMessage;
  const openStatus = (kind: 'hvac' | 'fan' | 'rangeStops' | 'permissions') => {
    setActiveStatus(kind);
    publishStatusMessage(BLANK);
  };
  const closeStatus = () => {
    setActiveStatus(undefined);
    showStatusMessage(BLANK);
  };
  const showPermissions = () =>
    thermostat?.isMitsubishiThermostat
      ? openStatus('permissions')
      : showStatusMessage(permissionHelp);
  return (
    <>
      <Box
        sx={{
          p: 0,
          padding: '0px',
          paddingTop: '0px',
          paddingBottom: statusMessage.trim().length ? '0px' : '0px',
          minHeight: '58px',
          height: wrapStatus ? undefined : '58px',
          backgroundColor: 'lightGray',
          position: 'relative',
        }}
      >
        {/* State Icons */}
        <Stack
          direction={'row'}
          justifyContent={'space-evenly'}
          divider={<Divider orientation="vertical" flexItem />}
          sx={{
            py: '2px',
            borderBottom: '1px solid lightGrey',
            backgroundColor: 'white',
          }}
        >
          <button
            type="button"
            aria-label={hvacHelp}
            onFocus={() => openStatus('hvac')}
            onBlur={closeStatus}
            onClick={() => openStatus('hvac')}
            onMouseEnter={() => openStatus('hvac')}
            onMouseLeave={(event) => {
              if (event.currentTarget !== document.activeElement) closeStatus();
            }}
            className={`zone-icon ${thermostat?.hvacIconClass?.join(' ')}`}
          >
            {thermostat?.hvacIconClass?.includes('zone-hvac-unknown') ? (
              <UnknownStatusIcon
                kind="hvac"
                label={
                  thermostat?.hvacIconHelpText ||
                  'Heating/cooling activity unknown'
                }
              />
            ) : (
              <>&nbsp;</>
            )}
          </button>
          <button
            type="button"
            aria-label={fanHelp}
            onFocus={() => openStatus('fan')}
            onBlur={closeStatus}
            onClick={() => openStatus('fan')}
            onMouseEnter={() => openStatus('fan')}
            onMouseLeave={(event) => {
              if (event.currentTarget !== document.activeElement) closeStatus();
            }}
            className={`zone-icon zone-fan-icon ${thermostat?.fanIconClass?.join(
              ' ',
            )}`}
          >
            {thermostat?.fanIconClass?.includes('zone-fan-unknown') ? (
              <UnknownStatusIcon
                kind="fan"
                label={
                  thermostat?.fanIconHelpText || 'Fan-running status unknown'
                }
              />
            ) : (
              <>&nbsp;</>
            )}
          </button>
          {supportsScheduling ? (
            <div
              onMouseOver={() =>
                showStatusMessage(thermostat?.scheduleIconHelpText || BLANK)
              }
              onMouseOut={() => showStatusMessage(BLANK)}
              className={`zone-icon ${thermostat?.scheduleIconClass?.join(
                ' ',
              )}`}
            >
              &nbsp;
            </div>
          ) : null}
          {showPermissionStatus && (
            <div
              role={thermostat?.isMitsubishiThermostat ? 'button' : undefined}
              tabIndex={thermostat?.isMitsubishiThermostat ? 0 : undefined}
              aria-label={
                thermostat?.isMitsubishiThermostat
                  ? 'Local controller permissions'
                  : undefined
              }
              title={
                thermostat?.isMitsubishiThermostat
                  ? undefined
                  : (thermostat?.keypadLockoutIconHelpText ?? undefined)
              }
              onFocus={showPermissions}
              onBlur={() => showStatusMessage(BLANK)}
              onClick={showPermissions}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  showPermissions();
                }
              }}
              onMouseOver={showPermissions}
              onMouseOut={(event) => {
                if (
                  thermostat?.isMitsubishiThermostat &&
                  event.currentTarget === document.activeElement
                )
                  return;
                showStatusMessage(BLANK);
              }}
              className={`zone-icon ${thermostat?.keypadLockoutIconClass?.join(
                ' ',
              )}`}
            >
              {thermostat?.keypadLockoutIconClass?.includes('zone-lock-unknown')
                ? '?'
                : '\u00a0'}
            </div>
          )}
          <div
            data-testid="setpoint-limits-icon"
            role={thermostat?.isMitsubishiThermostat ? 'button' : undefined}
            tabIndex={thermostat?.isMitsubishiThermostat ? 0 : undefined}
            aria-label={
              thermostat?.isMitsubishiThermostat
                ? useManualMitsubishiRangeStops
                  ? 'Range stops'
                  : 'Setpoint limits'
                : undefined
            }
            onFocus={
              thermostat?.isMitsubishiThermostat
                ? () => openStatus('rangeStops')
                : undefined
            }
            onBlur={
              thermostat?.isMitsubishiThermostat ? closeStatus : undefined
            }
            onClick={
              thermostat?.isMitsubishiThermostat
                ? () => openStatus('rangeStops')
                : undefined
            }
            onKeyDown={
              thermostat?.isMitsubishiThermostat
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openStatus('rangeStops');
                    }
                  }
                : undefined
            }
            onMouseOver={() => {
              if (thermostat?.isMitsubishiThermostat === true) {
                openStatus('rangeStops');
                return;
              }
              showStatusMessage(profileHelp());
            }}
            onMouseOut={(event) => {
              if (
                thermostat?.isMitsubishiThermostat &&
                event.currentTarget === document.activeElement
              )
                return;
              showStatusMessage(BLANK);
            }}
            className={`zone-icon ${thermostat?.profileIconClass?.join(' ')}`}
          >
            &nbsp;
          </div>
        </Stack>
        {displayedMessage.trim().length ? (
          <StatusMessage message={displayedMessage} wrap={wrapStatus} />
        ) : null}
        <div
          style={{
            position: wrapStatus ? 'relative' : 'absolute',
            zIndex: '1',
            height: '24px',
            width: '100%',
          }}
        >
          <span
            style={{
              display: 'flex',
              justifyContent: 'center',
              fontSize: '.75em',
              paddingTop: '3px',
            }}
          >
            {isSmall
              ? '* Tap an icon to view its status message.'
              : '* Hover over an icon to view its status message.'}
          </span>
        </div>
      </Box>
      {showResumeSchedule && (
        <>
          <hr
            style={{
              padding: 0,
              margin: 0,
              border: '0.5px solid lightgray',
            }}
          />
          <ResumeSchedule
            deviceId={thermostat?.deviceId || ''}
            briefScheduleStatus={briefScheduleStatus}
            holdEnd={thermostat?.getHoldEnd ?? undefined}
            updatePending={thermostat?.hasPendingUpdates}
          />
        </>
      )}
    </>
  );
}
